import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { EmbedViewerDto } from "../types/tree";
import {
  loadViewerJs,
  makeBridgeRef,
  type ViewerJsModule,
} from "../unity/webBridgeAdapter";

// Animation delays for the 3x3 spinkit-style grid: a wave that ripples through
// the cubes diagonally. Matches the spinkit "sk-grid" pattern used by Blazor's
// loader so the visual is consistent across the two stacks.
const gridDelays = [0.2, 0.3, 0.4, 0.1, 0.2, 0.3, 0.0, 0.1, 0.2];

export interface ConfiguratorViewerHandle {
  /**
   * Sends a message to the Unity scene via the WebBridge envelope path
   * (preferred). Use this after the viewer has loaded.
   */
  sendToUnity(method: string, payload?: string): Promise<void>;
  /**
   * Request-response variant. Unity replies via a callback id embedded in
   * the envelope; resolves with the raw JSON string Unity sent back.
   * Throws on timeout (default 15s) or when the JS bridge is missing.
   */
  callUnity(method: string, payload?: unknown, timeoutMs?: number): Promise<string>;
  /**
   * Legacy GameObject SendMessage path — kept for parity with the Blazor
   * SendMessageToViewerAsync. Most new code should prefer sendToUnity.
   */
  sendMessage(target: string, method: string, payload?: string): Promise<void>;
  /** True once Unity has finished loading and init messages have been sent. */
  isReady(): boolean;
}

export interface ConfiguratorViewerProps {
  /** Build info from the embed API. When null, renders an empty placeholder. */
  viewer: EmbedViewerDto | null;
  /**
   * Origin to load the configurator-viewer.js module from. Empty = same origin.
   * The BaseDashboard host serves it at `/base-dashboard/js/configurator-viewer.js`.
   */
  baseUrl?: string;
  /**
   * DEPRECATED / IGNORED. The API base URL Unity uses is always the origin the
   * embed is served from (window.location.origin) — the dashboard host that also
   * serves the API. The embed already knows its own origin, so this never needs
   * to be supplied, and passing it can't override the origin (that would let a
   * caller point Unity at the wrong/stale API). Kept only so existing callers
   * that still pass it don't break.
   */
  apiBaseUrl?: string;
  /** Optional callback invoked when the viewer has loaded and Ready was sent. */
  onReady?: () => void;
  /** Optional callback for typed Unity events (messageType starts with "event:"). */
  onUnityEvent?: (eventType: string, data: string) => void;
  /**
   * Async callback that returns a fresh access token. When Unity sends
   * "RequestTokenRefresh", this is called and the result is sent as SetAccessToken.
   * Default behavior (when omitted): no refresh — Unity gets the initial token only.
   */
  onRequestTokenRefresh?: () => Promise<string | null>;
  /**
   * When true, the wrapper calls initializeDragDrop + initializeHotkeys on the JS
   * module right after Ready, enabling the drag-products-into-Unity flow used by
   * the SceneDesigner tool. The ProductTreePreview tool leaves this off — its
   * options are clicked, not dragged, and hotkeys would steal browser focus.
   */
  enableDragAndHotkeys?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Wraps BaseDashboard/wwwroot/js/configurator-viewer.js for React. After Unity loads,
 * sends the same init sequence the Blazor ConfiguratorViewer does:
 * SetAccessToken (when present) → SetViewerId → Ready. Then registers with the
 * JS-side WebBridge so Unity → Blazor events route back through us.
 *
 * Lifecycle invariant: each effect run owns the Unity instance it created in a
 * local closure. Cleanup destroys *that* instance, never the next mount's. This
 * matters for React StrictMode (double-invoked effects) and for parents that pass
 * inline-arrow callbacks (which would otherwise re-trigger the effect every render).
 */
export const ConfiguratorViewer = forwardRef<ConfiguratorViewerHandle, ConfiguratorViewerProps>(
  function ConfiguratorViewer(
    { viewer, baseUrl = "", apiBaseUrl, onReady, onUnityEvent, onRequestTokenRefresh, enableDragAndHotkeys, className, style },
    ref
  ) {
    // The API Unity fetches from is ALWAYS the dashboard's configured live domain
    // (server-provided on the viewer DTO from Settings:Domain) — never the origin
    // the embed happens to be served from. This is what lets a test/staging
    // front-end still hit the correct (live) API instead of a test API that may
    // not have the assets. Falls back to the page origin only when no domain is
    // configured server-side (pure local dev). The `apiBaseUrl` prop is ignored —
    // a caller must not be able to point Unity at the wrong API.
    const effectiveApiBaseUrl =
      viewer?.apiBaseUrl ||
      (typeof window !== "undefined" ? window.location.origin : "");
    void apiBaseUrl;
    const reactId = useId().replace(/[:]/g, "");
    const containerId = `dconfig-viewer-${reactId}`;
    const canvasId = `dconfig-canvas-${reactId}`;
    // Element refs are passed to createViewer alongside the IDs so the JS module
    // can find the nodes even when they live inside a Shadow DOM (the embed),
    // where document.getElementById is blind.
    const containerElRef = useRef<HTMLDivElement | null>(null);
    const canvasElRef = useRef<HTMLCanvasElement | null>(null);

    const [progress, setProgress] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Stable refs to props that shouldn't re-trigger the effect when the parent
    // passes new closures every render. The effect reads .current at runtime.
    const onReadyRef = useRef(onReady);
    const onUnityEventRef = useRef(onUnityEvent);
    const onRequestTokenRefreshRef = useRef(onRequestTokenRefresh);
    onReadyRef.current = onReady;
    onUnityEventRef.current = onUnityEvent;
    onRequestTokenRefreshRef.current = onRequestTokenRefresh;

    // Active Unity instance for the *current* effect run. The imperative handle
    // (sendToUnity/sendMessage/isReady) reads through this so the parent always
    // talks to whichever instance is alive right now.
    const activeRef = useRef<{
      mod: ViewerJsModule;
      viewerId: string;
      ready: boolean;
    } | null>(null);

    const accessTokenRef = useRef<string | null>(null);
    accessTokenRef.current = viewer?.accessToken ?? null;

    // Captured by the effect so the visibility-change / drag-bridge-not-ready
    // self-heal can re-register without re-running the effect (which would
    // tear down Unity). Set right before registerWebBridgeViewer fires the
    // first time; cleared on unmount.
    const bridgeRefHolder = useRef<{
      bridgeRef: ReturnType<typeof makeBridgeRef> | null;
      methodName: string;
    }>({ bridgeRef: null, methodName: "HandleUnityMessage" });

    const sendToUnity = useCallback(async (method: string, payload: string = "") => {
      const a = activeRef.current;
      if (!a) return;
      if (!a.mod.sendToUnity) {
        console.warn("[ConfiguratorViewer] sendToUnity not available on the JS module — host needs update");
        return;
      }
      await a.mod.sendToUnity(a.viewerId, method, payload);
    }, []);

    const callUnity = useCallback(async (method: string, payload?: unknown, timeoutMs?: number) => {
      const a = activeRef.current;
      if (!a) throw new Error("Viewer not active");
      if (!a.mod.requestUnity) {
        throw new Error("requestUnity not available on the JS module — host needs update");
      }
      return await a.mod.requestUnity(a.viewerId, method, payload ?? {}, timeoutMs);
    }, []);

    const sendMessage = useCallback(async (target: string, method: string, payload: string = "") => {
      const a = activeRef.current;
      if (!a) return;
      await a.mod.sendMessage(a.viewerId, target, method, payload);
    }, []);

    useImperativeHandle(
      ref,
      (): ConfiguratorViewerHandle => ({
        sendToUnity,
        callUnity,
        sendMessage,
        isReady: () => activeRef.current?.ready === true,
      }),
      [sendToUnity, callUnity, sendMessage]
    );

    useEffect(() => {
      if (!viewer) return;

      // Per-mount state. The cleanup closes over these and destroys exactly what
      // this mount created — even if a second mount happened in StrictMode.
      let cancelled = false;
      let localMod: ViewerJsModule | null = null;
      let localViewerId: string | null = null;

      setProgress(0);
      setLoaded(false);
      setError(null);

      (async () => {
        try {
          const scriptUrl = `${baseUrl.replace(/\/+$/, "")}/base-dashboard/js/configurator-viewer.js`;
          const mod = await loadViewerJs(scriptUrl);
          if (cancelled) return;
          localMod = mod;

          // Wait one frame so React has flushed canvas/container to the DOM.
          await new Promise(requestAnimationFrame);
          if (cancelled) return;

          // Seed the canvas drawing-buffer to the real CSS size before Unity
          // boots. The HTML default is 300×150 — Unity reads that at boot to
          // size Camera.pixelRect, then matchWebGLToCanvasSize tracks the CSS
          // size each frame after. Without this seed the camera boots at
          // 300×150 and even after Unity matches the canvas attr to the
          // bigger CSS size, the boot framing has already been captured.
          const canvasEl = canvasElRef.current;
          const containerEl = containerElRef.current;
          if (canvasEl && containerEl) {
            const dpr = window.devicePixelRatio || 1;
            const w = Math.max(1, Math.round(containerEl.clientWidth * dpr));
            const h = Math.max(1, Math.round(containerEl.clientHeight * dpr));
            if (w > 1 && h > 1) {
              canvasEl.width = w;
              canvasEl.height = h;
            }
          }

          const id = crypto.randomUUID();
          localViewerId = id;
          activeRef.current = { mod, viewerId: id, ready: false };

          const bridgeRef = makeBridgeRef({
            OnViewerProgress: p => {
              if (cancelled) return;
              setProgress(typeof p === "number" ? p : 0);
            },
            OnViewerLoaded: () => {
              if (cancelled) return;
              (async () => {
                try {
                  if (mod.registerWebBridgeViewer) {
                    mod.registerWebBridgeViewer(id, bridgeRef, "HandleUnityMessage");
                    // Stash for the self-heal listeners installed below — they
                    // re-call register() if the bridge ever drifts (visibility
                    // change, HMR, parent remount that destroyed the registry
                    // entry but kept the Unity instance live).
                    bridgeRefHolder.current = { bridgeRef, methodName: "HandleUnityMessage" };
                  }
                  if (mod.sendToUnity) {
                    // Order matters: SetViewerId → SetApiBaseUrl → SetAccessToken → Ready.
                    // Unity needs the API base before any asset fetch fires, and the access
                    // token before any authenticated request lands. LoadProduct comes from
                    // the parent tool *after* Ready and after this whole sequence completes.
                    await mod.sendToUnity(id, "SetViewerId", JSON.stringify({ viewerId: id }));
                    if (effectiveApiBaseUrl) {
                      await mod.sendToUnity(
                        id,
                        "SetApiBaseUrl",
                        JSON.stringify({ url: effectiveApiBaseUrl })
                      );
                    }
                    if (accessTokenRef.current) {
                      await mod.sendToUnity(id, "SetAccessToken", JSON.stringify({ token: accessTokenRef.current }));
                    }
                    await mod.sendToUnity(id, "Ready", "");
                  }
                  // SceneDesigner-style tools need the DragDropManager + HotkeyManager
                  // active. Both are no-ops if the JS module hasn't exported them
                  // (older host build) — the tool just won't have drag/hotkey support.
                  if (enableDragAndHotkeys) {
                    try {
                      // Pass a non-existent sentinel id so the JS module's
                      //   document.getElementById(containerId) || document.body
                      // falls through to document.body. The Blazor configurator put
                      // both the viewer and the product catalog inside one wrapping
                      // container, but in the React layout the catalog is a sibling
                      // of the viewer — scoping the scan to containerId would miss
                      // every draggable card. Empty-string id triggers a browser
                      // deprecation warning, so we use a sentinel instead.
                      await mod.initializeDragDrop?.(id, "__dconfig-drag-root-doc__", canvasId);
                      await mod.initializeHotkeys?.(id);
                    } catch (err) {
                      console.warn("[ConfiguratorViewer] drag/hotkey init failed:", err);
                    }
                  }
                } catch (err) {
                  console.warn("[ConfiguratorViewer] init message dispatch failed:", err);
                } finally {
                  if (cancelled) return;
                  if (activeRef.current?.viewerId === id) activeRef.current.ready = true;
                  setLoaded(true);
                  onReadyRef.current?.();
                }
              })();
            },
            OnViewerError: e => {
              if (cancelled) return;
              setError(typeof e === "string" ? e : "Viewer error");
            },
            HandleUnityMessage: (messageType, data) => {
              if (cancelled || typeof messageType !== "string") return;
              if (messageType === "RequestTokenRefresh") {
                (async () => {
                  try {
                    const fresh = (await onRequestTokenRefreshRef.current?.()) ?? null;
                    if (!fresh || cancelled || !mod.sendToUnity) return;
                    accessTokenRef.current = fresh;
                    await mod.sendToUnity(id, "SetAccessToken", JSON.stringify({ token: fresh }));
                  } catch (err) {
                    console.warn("[ConfiguratorViewer] token refresh failed:", err);
                  }
                })();
                return;
              }
              if (messageType.startsWith("event:")) {
                const eventName = messageType.substring("event:".length);
                // Drag-spawn hand-off: when Unity has instantiated and positioned
                // the dragged product, tell the JS DragDropManager so it hides
                // its placeholder ghost. Pure pass-through — the JS module owns
                // the ghost element; we just flip its readiness flag.
                if (eventName === "UnityPreviewReady" && mod.setUnityPreviewReady) {
                  try { mod.setUnityPreviewReady(id, true); }
                  catch (err) { console.warn("[ConfiguratorViewer] setUnityPreviewReady failed:", err); }
                }
                onUnityEventRef.current?.(
                  eventName,
                  typeof data === "string" ? data : ""
                );
              }
            },
          });

          await mod.createViewer(
            {
              viewerId: id,
              version: viewer.version,
              containerId,
              canvasId,
              containerEl: containerElRef.current,
              canvasEl: canvasElRef.current,
              buildUrl: viewer.buildUrl,
              enableScroll: false,
              showLoadingBar: true,
              configuration: null,
            },
            bridgeRef
          );
        } catch (err) {
          if (!cancelled) setError(err instanceof Error ? err.message : String(err));
        }
      })();

      return () => {
        cancelled = true;
        const mod = localMod;
        const id = localViewerId;
        // Clear active before destroying so any in-flight sendToUnity is a no-op.
        if (activeRef.current?.viewerId === id) activeRef.current = null;
        if (!mod || !id) return;
        try {
          mod.unregisterWebBridgeViewer?.(id);
        } catch {
          /* ignore */
        }
        try {
          // destroyViewer may return undefined or a promise; either way swallow errors.
          const result = mod.destroyViewer(id) as unknown;
          if (result && typeof (result as Promise<unknown>).then === "function") {
            (result as Promise<unknown>).catch(() => {});
          }
        } catch {
          /* ignore — Unity may already be tearing down */
        }
      };
    }, [viewer?.buildUrl, viewer?.version, baseUrl, effectiveApiBaseUrl, containerId, canvasId, enableDragAndHotkeys]);

    // Bridge self-heal. The drag-fizzle symptom — "Unity is alive, camera
    // works, but dragging a product does nothing" — comes from the JS-side
    // WebBridge `viewers` Map losing our entry while the Unity instance is
    // still running. Triggers we've seen: tab backgrounded for hours,
    // duplicate register/unregister races during HMR, or a parent remount
    // pattern that closed over the old viewerId.
    //
    // The configurator-viewer.js IIFE raises three CustomEvents we react to:
    //   webbridge-drop            → a sendToUnity was made for an unknown viewerId
    //   webbridge-rebind-needed   → visibility probe found a drift (mgr has id, bridge doesn't)
    //   drag-bridge-not-ready     → drag manager bailed before showing a ghost
    //
    // All three converge on the same fix: re-register the current viewer with
    // the live bridgeRef. Idempotent — register() is a Map.set().
    useEffect(() => {
      function tryRebind() {
        const a = activeRef.current;
        const holder = bridgeRefHolder.current;
        if (!a || !a.mod || !holder.bridgeRef) return;
        try {
          a.mod.registerWebBridgeViewer?.(a.viewerId, holder.bridgeRef, holder.methodName);
        } catch (err) {
          console.warn("[ConfiguratorViewer] bridge rebind failed:", err);
        }
      }
      function onWebBridgeDrop(e: Event) {
        const detail = (e as CustomEvent).detail as { viewerId?: string };
        if (!detail || detail.viewerId !== activeRef.current?.viewerId) return;
        tryRebind();
      }
      function onRebindNeeded(e: Event) {
        const detail = (e as CustomEvent).detail as { viewerIds?: string[] };
        if (!detail || !Array.isArray(detail.viewerIds)) return;
        if (detail.viewerIds.includes(activeRef.current?.viewerId ?? "")) tryRebind();
      }
      function onDragBridgeNotReady(e: Event) {
        const detail = (e as CustomEvent).detail as { viewerId?: string };
        if (!detail || detail.viewerId !== activeRef.current?.viewerId) return;
        tryRebind();
      }
      window.addEventListener("webbridge-drop", onWebBridgeDrop);
      window.addEventListener("webbridge-rebind-needed", onRebindNeeded);
      window.addEventListener("drag-bridge-not-ready", onDragBridgeNotReady);
      return () => {
        window.removeEventListener("webbridge-drop", onWebBridgeDrop);
        window.removeEventListener("webbridge-rebind-needed", onRebindNeeded);
        window.removeEventListener("drag-bridge-not-ready", onDragBridgeNotReady);
      };
    }, []);


    if (!viewer) {
      return (
        <div className={"dconfig-viewer dconfig-viewer--placeholder " + (className ?? "")} style={style}>
          <div className="dconfig-viewer__placeholder-text">3D viewer (no designer target configured)</div>
        </div>
      );
    }

    return (
      <div id={containerId} ref={containerElRef} className={"dconfig-viewer " + (className ?? "")} style={style}>
        <div
          className={"dconfig-viewer__loading" + (loaded ? " dconfig-viewer__loading--done" : "")}
          aria-hidden={loaded}
        >
          <div className="dconfig-viewer__loading-inner">
            <div className="dconfig-viewer__sk-grid" aria-label="Loading">
              {Array.from({ length: 9 }).map((_, i) => (
                <span
                  key={i}
                  className="dconfig-viewer__sk-cube"
                  style={{ animationDelay: `${gridDelays[i]}s` }}
                />
              ))}
            </div>
            {error && <div className="dconfig-viewer__error">{error}</div>}
          </div>
        </div>
        <canvas id={canvasId} ref={canvasElRef} className="dconfig-viewer__canvas" />
        {/* Progress shown subtly in the corner — keeps the loader uncluttered. */}
        {!loaded && progress > 0 && progress < 1 && (
          <div className="dconfig-viewer__progress" aria-hidden="true">
            {Math.round(progress * 100)}%
          </div>
        )}
        {/* Unity build version, bottom-left. Matches the Blazor viewer's badge so
            it's obvious at a glance which build is actually running. */}
        {loaded && viewer.version && (
          <div className="dconfig-viewer__version" aria-hidden="true">{viewer.version}</div>
        )}
      </div>
    );
  }
);
